// =========================================
// MOBILE APP SEMANTIC COLOR SYSTEM
// =========================================
// This file contains the mobile app's color system
// Single source of truth for mobile color values

interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  primaryButton: string;
  primaryButtonHover?: string;
  secondaryButton: string;
  secondaryButtonHover?: string;
}

interface ActiveColors extends ColorScheme {
  gradient: string;
}

interface NewColorsType {
  // Core colors
  white: string;
  black: string;

  // Brand colors
  brand: {
    primary: string;
    secondary: string;
    accent: string;
  };

  // Triadic power scheme
  triadicPower: {
    primary: string;
    primaryDark: string;
    accent: string;
    powerAccent: string;
    secondaryGradient: {
      colors: string[];
      type: string;
      start: { x: number; y: number };
      end: { x: number; y: number };
    };
    secondaryStart: string;
    secondaryEnd: string;
    buttons: {
      primary: string;
      primaryHover: string;
      cta: string;
      ctaHover: string;
      urgent: string;
      urgentHover: string;
    };
    interactive: {
      hover: string;
      pressed: string;
      focus: string;
      disabled: string;
    };
  };

  // Status colors
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
  };

  // Backgrounds
  pageBackground: string;
  cardBackground: string;
  sectionBackground: string;
  sectionBackgroundTinted: {
    blue: string;
    warm: string;
    purple: string;
    green: string;
  };

  // Text hierarchy
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    muted: string;
    inverse: string;
  };

  // Typography
  typography: {
    h1: { fontSize: number; fontWeight: string };
    h2: { fontSize: number; fontWeight: string };
    h3: { fontSize: number; fontWeight: string };
    body: { fontSize: number; fontWeight: string };
    secondary: { fontSize: number; fontWeight: string };
    caption: { fontSize: number; fontWeight: string };
  };

  // Borders
  border: {
    light: string;
    medium: string;
    strong: string;
    dark: string;
  };

  // Domain-specific colors
  nutrition: {
    calories: string;
    caloriesBackground: string;
    proteins: string;
    proteinsBackground: string;
    carbs: string;
    carbsBackground: string;
    fats: string;
    fatsBackground: string;
  };

  training: {
    cardioRed: string;
    cardioTeal: string;
    cardioBlue: string;
    cardioGreen: string;
  };

  charts: {
    series1: string;
    series2: string;
    series3: string;
    series4: string;
    series5: string;
    series6: string;
    warning: string;
    alternative: string;
    getOrderedColors: () => string[];
    getColorByIndex: (index: number) => string;
  };

  utility: {
    shadow: string;
    overlay: string;
    divider: string;
    placeholder: string;
    outline: string;
  };

  // Utility functions
  activeTheme: string;
  getActiveScheme: (scheme?: string) => ColorScheme;
  getTriadicGradient: (direction?: string) => string;
  getChartColors: (count?: number) => string[];
  getActive: () => ActiveColors;

  // Active theme interface
  active: ActiveColors;
}

const NewColors: NewColorsType = {
  // =========================================
  // CORE SYSTEM COLORS
  // =========================================

  // Base colors
  white: "#fff",
  black: "#000",

  // =========================================
  // SEMANTIC COLOR SYSTEM
  // =========================================

  // Brand Colors (Original)
  brand: {
    primary: "#3B82F6", // Deep navy - main brand color
    secondary: "#EA580C", // Warm coral - secondary brand
    accent: "#D97706", // Warm amber - accent color
  },

  // =========================================
  // TRIADIC POWER COLOR SCHEME
  // =========================================
  triadicPower: {
    // Primary Colors
    primary: "#2E5266", // Sophisticated Teal - main brand color
    primaryDark: "#1E3A47", // Deep Teal - for gradients/shadows

    // Accent Colors
    accent: "#FF7A5A", // Energetic Rust Orange - main CTAs and highlights
    powerAccent: "#B91C1C", // Bold Cardinal Red - urgent actions, success states

    // Gradient Definition for Secondary Color
    secondaryGradient: {
      colors: ["#FF7A5A", "#B91C1C"], // Orange to Red gradient
      type: "linear",
      start: { x: 0, y: 0 },
      end: { x: 1, y: 0 },
    },

    // Individual gradient colors for non-gradient contexts
    secondaryStart: "#FF7A5A", // Start of gradient
    secondaryEnd: "#B91C1C", // End of gradient

    // Usage-specific variants
    buttons: {
      primary: "#2E5266", // Headers, navigation, primary buttons
      primaryHover: "#1E3A47", // Hover state for primary buttons
      cta: "#FF7A5A", // Call-to-action buttons, highlights
      ctaHover: "#E56B4A", // Hover state for CTA buttons
      urgent: "#B91C1C", // Urgent actions, critical buttons
      urgentHover: "#9F1616", // Hover state for urgent buttons
    },

    // Interactive states with triadic colors
    interactive: {
      hover: "rgba(46, 82, 102, 0.1)", // Light teal hover
      pressed: "rgba(46, 82, 102, 0.2)", // Darker teal pressed
      focus: "#FF7A5A", // Orange focus ring
      disabled: "#9CA3AF", // Keep existing disabled color
    },
  },

  // Status Colors
  status: {
    success: "#059669", // Green for success states
    warning: "#F7B801", // Amber for warnings
    error: "#DC5A47", // Warm coral-red for errors (not aggressive)
    info: "#0EA5E9", // Calming teal for info
  },

  // =========================================
  // BACKGROUND SYSTEM (3-Layer Hierarchy)
  // =========================================

  // Layer 1: Page backgrounds
  pageBackground: "#F3F4F8", // Main page/screen background

  // Layer 2: Content containers (cards, panels)
  cardBackground: "#FFFFFF", // Primary content cards

  // Layer 3: Subtle sections within cards
  sectionBackground: "#F9FAFB", // Subtle sections, input fields
  sectionBackgroundTinted: {
    blue: "#FAFBFF", // Nutrition sections
    warm: "#FFFBF5", // Meal plan, cardio sections
    purple: "#FDFBFF", // Supplement sections
    green: "#F8FFF8", // Wellness sections
  },

  // =========================================
  // TEXT HIERARCHY SYSTEM
  // =========================================

  text: {
    primary: "#1F2937", // Main headings, primary text
    secondary: "#6B7280", // Secondary text, descriptions
    tertiary: "#9CA3AF", // Captions, labels, timestamps
    muted: "#9CA3AF", // Disabled or less important text
    inverse: "#FFFFFF", // Text on dark backgrounds
  },

  // =========================================
  // TYPOGRAPHY SCALE SYSTEM
  // =========================================

  typography: {
    h1: { fontSize: 28, fontWeight: "bold" }, // Page titles - use text.primary
    h2: { fontSize: 20, fontWeight: "bold" }, // Section headers - use text.primary
    h3: { fontSize: 18, fontWeight: "600" }, // Card titles - use text.primary
    body: { fontSize: 16, fontWeight: "normal" }, // Main content - use text.primary
    secondary: { fontSize: 14, fontWeight: "normal" }, // Descriptions - use text.secondary
    caption: { fontSize: 12, fontWeight: "normal" }, // Labels, timestamps - use text.tertiary
  },

  // =========================================
  // BORDER SYSTEM
  // =========================================

  border: {
    light: "#F3F4F6", // Very subtle borders
    medium: "#E5E7EB", // Standard borders
    strong: "#D1D5DB", // Emphasized borders
    dark: "#9CA3AF", // Strong contrast borders
  },

  // =========================================
  // DOMAIN-SPECIFIC COLORS
  // =========================================

  // Nutrition/Macro Colors
  nutrition: {
    calories: "#1E40AF", // Deep navy
    caloriesBackground: "#DBEAFE",
    proteins: "#EA580C", // Warm coral
    proteinsBackground: "#FED7AA",
    carbs: "#D97706", // Warm amber
    carbsBackground: "#FED7AA",
    fats: "#7C3AED", // Sophisticated purple
    fatsBackground: "#E9D5FF",
  },

  // Training/Cardio Colors
  training: {
    cardioRed: "#EF4444", // Bright but not harsh red
    cardioTeal: "#14B8A6", // Calming teal
    cardioBlue: "#3B82F6", // Professional blue
    cardioGreen: "#10B981", // Success green
  },

  // Chart/Data Visualization Colors (Ordered by priority)
  charts: {
    // Primary chart colors - semantic naming by priority
    series1: "#3B82F6", // 1st priority: Primary data series
    series2: "#F97316", // 2nd priority: Secondary data series
    series3: "#22C55E", // 3rd priority: Success/positive metrics
    series4: "#8B5CF6", // 4th priority: Alternative data series
    series5: "#14B8A6", // 5th priority: Supporting data
    series6: "#EC4899", // 6th priority: Accent data series

    // Additional chart colors (use sparingly)
    warning: "#EAB308", // Warning/attention metrics
    alternative: "#6366F1", // Deep alternative

    // Utility method to get ordered colors for multi-series charts
    getOrderedColors: (): string[] => [
      "#3B82F6",
      "#F97316",
      "#22C55E",
      "#8B5CF6",
      "#14B8A6",
      "#EC4899",
      "#EAB308",
      "#6366F1",
    ],

    // Get color by index (cycles through array)
    getColorByIndex: (index: number): string => {
      const colors = NewColors.charts.getOrderedColors();
      return colors[index % colors.length];
    },
  },

  // =========================================
  // UTILITY COLORS
  // =========================================

  utility: {
    shadow: "#000000",
    overlay: "rgba(0, 0, 0, 0.5)",
    divider: "#F3F4F6",
    placeholder: "#9CA3AF",
    outline: "#E5E7EB",
  },

  // =========================================
  // UTILITY FUNCTIONS
  // =========================================

  // Active theme configuration - change this to switch themes globally
  activeTheme: "triadicPower", // Options: 'brand', 'triadicPower'

  // Method to get current active color scheme
  getActiveScheme: (scheme = "brand"): ColorScheme => {
    const schemes = {
      brand: {
        primary: NewColors.brand.primary,
        secondary: NewColors.brand.secondary,
        accent: NewColors.brand.accent,
        primaryButton: NewColors.status.error, // Fallback to semantic color
        secondaryButton: NewColors.brand.secondary, // Use brand secondary
      },
      triadicPower: {
        primary: NewColors.triadicPower.primary,
        secondary: NewColors.triadicPower.accent,
        accent: NewColors.triadicPower.powerAccent,
        primaryButton: NewColors.triadicPower.buttons.primary,
        secondaryButton: NewColors.triadicPower.buttons.cta,
      },
    };
    return schemes[scheme as keyof typeof schemes] || schemes.brand;
  },

  // Method to get triadic power gradient CSS
  getTriadicGradient: (direction = "to right"): string => {
    return `linear-gradient(${direction}, ${NewColors.triadicPower.secondaryStart}, ${NewColors.triadicPower.secondaryEnd})`;
  },

  // Get chart colors for multi-series data
  getChartColors: (count = 6): string[] => {
    const colors = NewColors.charts.getOrderedColors();
    return colors.slice(0, Math.min(count, colors.length));
  },

  // Method to get active theme colors
  getActive: (): ActiveColors => {
    const activeScheme = NewColors.getActiveScheme(NewColors.activeTheme);
    return {
      primary: activeScheme.primary,
      secondary: activeScheme.secondary,
      accent: activeScheme.accent,
      primaryButton: activeScheme.primaryButton,
      secondaryButton: activeScheme.secondaryButton,

      // Theme-specific features
      gradient:
        NewColors.activeTheme === "triadicPower"
          ? NewColors.getTriadicGradient()
          : `linear-gradient(to right, ${NewColors.brand.primary}, ${NewColors.brand.secondary})`,
    };
  },

  // This will be initialized after the object is created
  active: {} as ActiveColors,
};

// =========================================
// ACTIVE THEME INTERFACE
// =========================================
// Initialize active property - this will be computed once at module load
NewColors.active = NewColors.getActive();

export default NewColors;
